import React, { useContext } from "react";
import { Form, Input, Button, Typography, message } from "antd";
import { MailOutlined, LockOutlined, ArrowRightOutlined, ScheduleOutlined, ClockCircleOutlined, BellOutlined } from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { AuthContext } from "../../context/AuthContext";
import { requestPushSubscription, registerPushSubscription } from "../../helper/pushNotifications";

const { Title } = Typography;

const LogoMark = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="9" fill="var(--i-ems-primary)" />
    <path d="M10 16.5l4 4 8-8.5" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LogoMarkLight = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="32" height="32" rx="9" fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.3)" />
    <path d="M10 16.5l4 4 8-8.5" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Deliberately no geofencing/location item here — that's the one thing
// feedback asked to keep off this page, even though it's a real feature.
const features = [
  {
    icon: <ScheduleOutlined />,
    text: "Leave, WFH & holidays",
    sub: "Apply and track approvals without the back-and-forth",
  },
  {
    icon: <ClockCircleOutlined />,
    text: "Attendance at a glance",
    sub: "See your login history and status anytime",
  },
  {
    icon: <BellOutlined />,
    text: "Stay in the loop",
    sub: "Real-time alerts the moment something needs your attention",
  },
];

const Login = () => {
  const { request } = useRequest();
  const { setIsLoggedIn, setUserProfile } = useContext(AuthContext);
  const navigate = useNavigate();

  const onFinish = (values) => {
    // Started immediately on the Sign In click, in parallel with the login
    // request below — NOT chained after it resolves. A browser's "user
    // gesture" window is short enough that waiting for a full login
    // round-trip to the server before triggering the permission prompt
    // reliably misses it (confirmed: the popup never appeared automatically
    // that way). This only creates the browser-side subscription — no
    // backend call yet, since there's no auth token until login succeeds.
    const pushSubscriptionPromise =
      typeof Notification !== "undefined" && Notification.permission === "default"
        ? requestPushSubscription()
        : Promise.resolve(null);

    request({
      url: apiPath.login,
      method: "POST",
      data: values,
      onSuccess: (res) => {
        if (res?.status === 200) {
          localStorage.setItem("i_ems_employee_token", res.data.token);
          localStorage.setItem("i_ems_employee_profile", JSON.stringify(res.data.profile));
          setUserProfile(res.data.profile);
          setIsLoggedIn(true);
          message.success("Logged in successfully");
          navigate("/");
          // Now that a valid token exists, register whatever subscription
          // (if any) the permission prompt above resulted in.
          pushSubscriptionPromise.then((sub) => registerPushSubscription(request, sub));
        } else {
          message.error(res?.message || "Login failed");
        }
      },
      onError: (err) => {
        message.error(err?.response?.data?.message || "Invalid email or password");
      },
    });
  };

  return (
    <div className="i-ems-login-page">
      <div className="i-ems-login-brand">
        <span className="i-ems-login-blob i-ems-login-blob-1" />
        <span className="i-ems-login-blob i-ems-login-blob-2" />
        <span className="i-ems-login-blob i-ems-login-blob-3" />

        <div className="i-ems-login-brand-top">
          <LogoMarkLight />
          <span className="i-ems-login-brand-wordmark">I-EMS</span>
        </div>

        <div className="i-ems-login-brand-mid">
          <h1 className="i-ems-login-brand-headline">Your workday, all in one place.</h1>
          <p className="i-ems-login-brand-copy">
            Attendance, leave and everything in between — one simple place to manage your day at
            work.
          </p>
          <div className="i-ems-login-feature-list">
            {features.map((f) => (
              <div className="i-ems-login-feature" key={f.text}>
                <span className="i-ems-login-feature-icon">{f.icon}</span>
                <div>
                  <div className="i-ems-login-feature-text">{f.text}</div>
                  <div className="i-ems-login-feature-sub">{f.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="i-ems-login-brand-footer">© {new Date().getFullYear()} Imergey · Employee Management Suite</div>
      </div>

      <div className="i-ems-login-form-panel">
        <span className="i-ems-login-form-glow-1" />
        <span className="i-ems-login-form-glow-2" />
        <div className="i-ems-login-card">
          <div className="i-ems-login-logo">
            <LogoMark />
            <span className="i-ems-login-logo-wordmark">I-EMS</span>
          </div>
          <Title level={3} className="i-ems-login-title">
            Welcome back
          </Title>
          <div className="i-ems-login-subtitle">Sign in to manage your attendance</div>
          <Form layout="vertical" onFinish={onFinish}>
            <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
              <Input size="large" prefix={<MailOutlined style={{ color: "var(--i-ems-text-secondary)" }} />} placeholder="you@company.com" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, min: 6 }]}
              extra={
                <div style={{ textAlign: "right", marginTop: 4 }}>
                  <Link to="/forgot-password" style={{ fontSize: 12.5 }}>
                    Forgot password?
                  </Link>
                </div>
              }
            >
              <Input.Password size="large" prefix={<LockOutlined style={{ color: "var(--i-ems-text-secondary)" }} />} placeholder="••••••••" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" size="large" block>
                Sign in <ArrowRightOutlined />
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Login;
